import { AppState, SCHEMA_VERSION } from "../types/state";

const STORAGE_SHEET = "__DIFFAPP_DATA__";

function migrate(raw: any): AppState {
  const config = raw.config ?? {};
  if (!Array.isArray(config.keyColumns)) {
    if (typeof config.keyColumn === "string" && config.keyColumn) {
      config.keyColumns = [config.keyColumn];
    } else {
      config.keyColumns = [];
    }
    delete config.keyColumn;
  }
  if (typeof config.headerRowCount !== "number" || config.headerRowCount < 1) {
    config.headerRowCount = 1;
  }
  if (typeof config.topRowsToSkip !== "number" || config.topRowsToSkip < 0) {
    config.topRowsToSkip = 0;
  }
  return { ...raw, config } as AppState;
}
const CHUNK_SIZE = 30000;
const MAX_CHUNKS = 200;

export async function loadState(): Promise<AppState | null> {
  return Excel.run(async (ctx) => {
    const sheets = ctx.workbook.worksheets;
    const sheet = sheets.getItemOrNullObject(STORAGE_SHEET);
    await ctx.sync();
    if (sheet.isNullObject) return null;

    const range = sheet.getRange(`A1:A${MAX_CHUNKS}`);
    range.load("values");
    await ctx.sync();

    const parts: string[] = [];
    const values = (range.values ?? []) as unknown[][];
    for (const row of values) {
      const v = String(row[0] ?? "");
      if (!v) break;
      parts.push(v);
    }
    const raw = parts.join("");
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      if (parsed.schemaVersion !== SCHEMA_VERSION) {
        console.error(`Schéma inconnu : ${parsed.schemaVersion}`);
        return null;
      }
      return migrate(parsed) as AppState;
    } catch (e) {
      console.error("Stockage corrompu :", e);
      return null;
    }
  });
}

export async function saveState(state: AppState): Promise<void> {
  const raw = JSON.stringify(state);
  const chunks: string[] = [];
  for (let i = 0; i < raw.length; i += CHUNK_SIZE) {
    chunks.push(raw.slice(i, i + CHUNK_SIZE));
  }
  if (chunks.length > MAX_CHUNKS) {
    throw new Error(`État trop volumineux (${chunks.length} cellules, max ${MAX_CHUNKS}).`);
  }

  return Excel.run(async (ctx) => {
    const sheets = ctx.workbook.worksheets;
    let sheet = sheets.getItemOrNullObject(STORAGE_SHEET);
    await ctx.sync();

    if (sheet.isNullObject) {
      sheet = sheets.add(STORAGE_SHEET);
      await ctx.sync();
    }

    sheet.visibility = Excel.SheetVisibility.visible;
    await ctx.sync();

    const clearRange = sheet.getRange(`A1:A${MAX_CHUNKS}`);
    clearRange.clear();
    await ctx.sync();

    const writeRange = sheet.getRange(`A1:A${chunks.length}`);
    writeRange.values = chunks.map((c) => [c]);
    await ctx.sync();

    sheet.visibility = Excel.SheetVisibility.veryHidden;
    await ctx.sync();
  });
}

export async function storageSheetExists(): Promise<boolean> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItemOrNullObject(STORAGE_SHEET);
    await ctx.sync();
    return !sheet.isNullObject;
  });
}

export const STORAGE_SHEET_NAME = STORAGE_SHEET;
